/** ----------------------------------------
    Fund Tool
 ---------------------------------------- */

import anime from 'animejs';

export class FundTool {

    /** ----------------------------------------
        Constructor
     ---------------------------------------- */

    constructor(elem) {
        this.elem = elem;

        this.startElem = document.querySelector('.js-fund-tool-start');
        this.stepsElem = this.elem.querySelector('.js-fund-tool-steps');
        this.stepElems = this.elem.querySelectorAll('.fund-tool__step');
        this.introElem = this.elem.querySelector('.js-fund-tool-intro');
        this.typeElem = this.elem.querySelector('.js-fund-tool-type');
        this.subtypeElems = this.elem.querySelectorAll('.js-fund-tool-subtype');
        this.amountElem = this.elem.querySelector('.js-fund-tool-amount');
        this.questionElems = this.elem.querySelectorAll('.js-fund-tool-question');
        this.resultsElem = this.elem.querySelector('.js-fund-tool-results');
        this.noResultsElem = this.elem.querySelector('.js-fund-tool-no-results');
        this.fundElems = this.elem.querySelectorAll('.js-fund-tool-fund');
        this.countElem = this.elem.querySelector('.js-fund-tool-count');
        this.resetElems = this.elem.querySelectorAll('.js-fund-tool-reset');
        this.backElem = this.elem.querySelector('.js-fund-tool-back');
        this.closeElem = this.elem.querySelector('.js-fund-tool-close');
        this.funds = new Map();
        this.filteredFunds = new Map();

        this.init();
    }

    /** ----------------------------------------
        Create map of funds
     ---------------------------------------- */

    createFundMap() {
        for (let fundElem of this.fundElems) {
            const dataset = fundElem.dataset;
            const fund = {
                elem: fundElem,
                id: parseInt(dataset.id, 10),
                subtypes: JSON.parse(dataset.subtypes),
                amountStart: parseFloat(dataset.amountStart, 10),
                amountEnd: parseFloat(dataset.amountEnd, 10),
            }
            this.funds.set(fund.id, fund);
        }
    }

    /** ----------------------------------------
        Init event listeners
     ---------------------------------------- */

    initIntroStepListeners() {
        const continueElem = this.introElem.querySelector('.js-fund-tool-continue');
        const agreeCheckboxElem = this.introElem.querySelector('.js-fund-tool-agree-checkbox');

        continueElem.addEventListener('click', e => {
            e.preventDefault();
            if (agreeCheckboxElem.checked) {
                this.data.agreed = true;
                this.filterFunds();
            }
        });
    }

    initTypeStepListeners() {
        const optionElems = this.typeElem.querySelectorAll('.js-fund-tool-select');

        for (let optionElem of optionElems) {
            optionElem.addEventListener('click', e => {
                e.preventDefault();
                this.data.type = parseInt(optionElem.dataset.type, 10);
                this.filterFunds();
            });
        }
    }

    initAmountStepListeners() {
        const continueElem = this.amountElem.querySelector('.js-fund-tool-continue');
        const skipElem = this.amountElem.querySelector('.js-fund-tool-skip');
        const amountInputElem = this.amountElem.querySelector('.js-fund-tool-amount-input');

        skipElem.addEventListener('click', e => {
            e.preventDefault();
            this.data.amount = 'skip';
            this.filterFunds();
        });

        continueElem.addEventListener('click', e => {
            e.preventDefault();
            if (amountInputElem.value) {
                this.data.amount = parseFloat(amountInputElem.value);
                this.filterFunds();
            }
        });
    }

    initSubtypeStepListeners() {
        for (let subtypeElem of this.subtypeElems) {
            const optionElems = subtypeElem.querySelectorAll('.js-fund-tool-select');

            for (let optionElem of optionElems) {
                optionElem.addEventListener('click', e => {
                    e.preventDefault();
                    this.data.subtype = parseInt(optionElem.dataset.subtype, 10);
                    this.filterFunds();
                });
            }
        }
    }

    initQuestionStepListeners() {
        for (let questionElem of this.questionElems) {
            const id = questionElem.id;
            const continueElem = questionElem.querySelector('.js-fund-tool-continue');
            const declineElem = questionElem.querySelector('.js-fund-tool-decline');
            const model = questionElem.dataset.model;
            const value = parseInt(questionElem.dataset[model], 10);

            continueElem.addEventListener('click', e => {
                e.preventDefault();
                this.data.questions[id] = true;
                this.filterFunds();
            });

            declineElem.addEventListener('click', e => {
                e.preventDefault();
                this.data.questions[id] = false;

                if (model === 'fund') {
                    this.data.skipFunds.push(value);
                } else {
                    this.data.skipFunds = Array.from(this.funds).map(fund => fund[1].id);
                }

                this.filterFunds();
            });
        }
    }

    initActionListeners() {
        for (let resetElem of this.resetElems) {
            resetElem.addEventListener('click', e => {
                e.preventDefault();
                this.reset();
            });
        }

        // this.startElem.addEventListener('click', e => {
        //     e.preventDefault();
        //     this.start();
        // });

        // this.closeElem.addEventListener('click', e => {
        //     e.preventDefault();
        //     this.close();
        // });

        this.backElem.addEventListener('click', e => {
            e.preventDefault();
            this.back();
        });
    }

    /** ----------------------------------------
        Filter funds
     ---------------------------------------- */

    filterFunds() {
        this.filteredFunds = new Map();

        this.funds.forEach(fund => {

            fund.elem.classList.remove('is-active');

            if (this.data.skipFunds.includes(fund.id))
                return;

            if (this.data.amount && this.data.amount !== 'skip' && (fund.amountStart > this.data.amount || fund.amountEnd <= this.data.amount))
                return;

            if (this.data.subtype && !fund.subtypes.includes(this.data.subtype) )
                return;

            fund.elem.classList.add('is-active');

            this.filteredFunds.set(fund.id, fund);
        });

        this.countElem.innerHTML = this.filteredFunds.size;

        this.updateView();
    }

    /** ----------------------------------------
        Update view
     ---------------------------------------- */

    updateView() {
        this.hideStepElems();

        if (!this.filteredFunds.size) {
            this.showNoResults();
        }

        if (!this.data.agreed) {
            this.showStep(this.introElem);
            return;
        }

        if (!this.data.type) {
            this.showStep(this.typeElem);
            return;
        }

        if (!this.data.amount) {
            this.showStep(this.amountElem);
            return;
        }

        if (!this.data.subtype) {
            this.showSubtype();
            return;
        }

        let done = true;

        for (let questionElem of this.questionElems) {
            const id = questionElem.id;

            if (this.data.questions[id] !== undefined) continue;

            const model = questionElem.dataset.model;
            const value = parseInt(questionElem.dataset[model], 10);

            if (model === 'type' && this.data.type === value) {
                this.showQuestion(questionElem);
                done = false;
                break;
            }

            if (model === 'subtype' && this.data.subtype === value) {
                this.showQuestion(questionElem);
                done = false;
                break;
            }

            if (model === 'fund' && this.filteredFunds.has(value)) {
                this.showQuestion(questionElem);
                done = false;
                break;
            }
        }

        if (done) {
            if (this.filteredFunds.size) {
                this.showResults();
            } else {
                this.showNoResults();
            }
        }
    }

    /** ----------------------------------------
        Show views
     ---------------------------------------- */

    showStep(target) {
        target.classList.add('is-active');
    }

    showSubtype() {
        for (let elem of this.subtypeElems) {
            const data = parseInt(elem.dataset.type, 19);

            if (data === this.data.type)
                elem.classList.add('is-active');
        }
    }

    showQuestion(questionElem) {
        questionElem.classList.add('is-active');
    }

    showSteps() {
        this.stepsElem.classList.add('is-active');
        this.hideResults();
        this.hideNoResults();
    }

    showResults() {
        this.resultsElem.classList.add('is-active');
        this.hideSteps();
        this.hideNoResults();
        this.resultsElem.scrollIntoView({behavior: "smooth", block: "end"});
    }

    showNoResults() {
        this.noResultsElem.classList.add('is-active');
        this.hideResults();
        this.hideSteps();
    }

    /** ----------------------------------------
        Hide views
     ---------------------------------------- */

    hideStepElems() {
        for (let stepElem of this.stepElems) {
            stepElem.classList.remove('is-active');
        }
    }

    hideSteps() {
        this.stepsElem.classList.remove('is-active');
    }

    hideResults() {
        this.resultsElem.classList.remove('is-active');
    }

    hideNoResults() {
        this.noResultsElem.classList.remove('is-active');
    }

    /** ----------------------------------------
        Actions
     ---------------------------------------- */

    start(pushState = true) {
        this.elem.classList.add('is-active');

        if (pushState)
            history.pushState('', document.title, `${window.location.pathname}${window.location.search}#subsidiewijzer`);

        const scrollElement = window.document.scrollingElement || window.document.body || window.document.documentElement;
        const scrollTo = this.elem.getBoundingClientRect().top + scrollElement.scrollTop - 160;

        anime({
            targets: scrollElement,
            scrollTop: scrollTo,
            duration: 500,
            easing: 'easeInOutQuad'
        });
    }

    reset() {
        this.data = {
            agreed: null,
            type: null,
            subtype: null,
            amount: null,
            questions: {},
            skipFunds: [],
        };

        this.filterFunds();
        this.showSteps();
    }

    back() {
        this.hideStepElems();

        if (!this.data.type) {
            this.showStep(this.introElem);
            return;
        }

        if (this.data.questions.length) {
            this.data.questions = [];
            this.data.skipFunds = [];
        } else if (this.data.subtype) {
            this.data.subtype = null;
        } else if (this.data.amount) {
            this.data.amount = null;
        } else if (this.data.type) {
            this.data.type = null;
        }

        this.filterFunds();
    }

    close(pushState = true) {
        this.elem.classList.remove('is-active');
        this.reset();

        if (pushState)
            history.pushState('', document.title, `${window.location.pathname}${window.location.search}`);
    }

    /** ----------------------------------------
        Init
     ---------------------------------------- */

    init() {
        this.reset();
        this.showSteps();
        this.createFundMap();
        this.initIntroStepListeners();
        this.initTypeStepListeners();
        this.initAmountStepListeners();
        this.initSubtypeStepListeners();
        this.initQuestionStepListeners();
        this.initActionListeners();
        this.filterFunds();

        // if (window.location.hash === '#subsidiewijzer') {
        //     this.start();
        // }

        // window.addEventListener('hashchange', () => {
        //     (window.location.hash === '#subsidiewijzer') ? this.start(false) : this.close(false);
        // }, false);
    }
}
